<script setup lang="ts">
import { computed } from 'vue'

import clsx from 'clsx'

import notification from '@/services/notification'

import BetaEditorPageChip from '@/components/beta-editor/BetaEditorPageChip.vue'
import { useEditorPageStore } from '@/stores/editor/page'

const editorPageStore = useEditorPageStore()

const emit = defineEmits<{
    leaveEditor: []
}>()

const loadingGetPage = computed(() => editorPageStore.loadingGetPage)
const post = computed(() => editorPageStore.activePage)

const steps = [
    { id: 1, label: 'Conteúdo' },
    { id: 2, label: 'Configuração e publicação' },
]

const onClickNext = async () => {
    if (editorPageStore.postStep === 1) {
        const isBlank = await editorPageStore.checkIsBlankPost()

        if (isBlank) {
            notification.simple('Preencha os campos de texto para continuar.', 'error')
            return
        }
    }

    editorPageStore.nextPostStep()
}

const onClickPublish = async () => {
    await editorPageStore.publishPost({
        sketch: 0,
        sanitizeBetaPayload: true,
        redirectTo: '/dashboard?beta',
        notification: {
            loading: editorPageStore.schedule ? 'Agendando...' : 'Publicando...',
            success: editorPageStore.schedule ? 'Post agendado com sucesso' : 'Post publicado com sucesso',
            error: editorPageStore.schedule ? 'Erro ao agendar post' : 'Erro ao publicar post',
        },
    })
}

const goBack = async () => {
    if (editorPageStore.postStep > 1) {
        editorPageStore.previousStep()
        return
    }

    emit('leaveEditor')
}
</script>

<template>
    <div class="post-beta-editor-header post-beta-editor-header--with-panel-navigation">
        <div class="post-beta-editor-header__left">
            <button class="button-back" @click="goBack">
                <span class="sr-only">Voltar</span>
                <svg class="h-4 w-4" viewBox="0 0 16 16" fill="none">
                    <path d="M4.63552 3.20005L1.59961 6.27704M1.59961 6.27704L4.63552 9.35404M1.59961 6.27704L11.1381 6.27704C12.9394 6.27704 14.3996 7.73727 14.3996 9.53855C14.3996 11.3398 12.9394 12.8 11.1381 12.8L7.99961 12.8" stroke="currentColor" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </button>

            <beta-editor-page-chip :title="post?.title" :loading="loadingGetPage" type="Post" />
        </div>

        <div class="post-beta-editor-header__center">
            <div class="stepper">
                <div
                    v-for="step in steps"
                    :key="step.id"
                    :class="clsx('stepper__item', editorPageStore.postStep >= step.id && 'is-active')"
                >
                    <span class="stepper__number">{{ step.id }}</span>
                    <span class="stepper__label">{{ step.label }}</span>
                </div>
            </div>
        </div>

        <div class="post-beta-editor-header__right">
            <span class="status-pill">Post</span>

            <button
                v-if="editorPageStore.postStep === 1"
                @click="onClickNext"
                class="button-primary"
            >
                Próximo
            </button>

            <button
                v-else
                @click="onClickPublish"
                class="button-primary"
                :disabled="loadingGetPage || editorPageStore.disabledPublish"
            >
                {{ editorPageStore.schedule ? 'Agendar' : 'Publicar' }}
            </button>
        </div>
    </div>
</template>

<style scoped>
.post-beta-editor-header {
    @apply fixed inset-x-0 top-0 z-50 grid h-[96px] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 border-b border-[#E3EAF4] bg-[rgba(245,247,251,0.92)] px-4 backdrop-blur-md md:px-8 lg:px-10;
}

.post-beta-editor-header--with-panel-navigation {
    left: 255px;
}

.post-beta-editor-header__left,
.post-beta-editor-header__right {
    @apply flex items-center gap-4;
}

.post-beta-editor-header__right {
    @apply justify-end;
}

.button-back {
    @apply flex h-11 w-11 items-center justify-center rounded-full border border-[#DCE5F2] bg-white text-[#44566F] shadow-sm;
}

.stepper {
    @apply flex items-center gap-2 rounded-full border border-[#DCE5F2] bg-white px-2 py-2 shadow-sm;
}

.stepper__item {
    @apply flex items-center gap-2 rounded-full px-3 py-2 text-[#7D8EA8];
}

.stepper__item.is-active {
    @apply bg-[#EEF4FF] text-[#10233E];
}

.stepper__number {
    @apply flex h-6 w-6 items-center justify-center rounded-full border border-current text-[11px] font-bold;
}

.stepper__label {
    @apply text-xs font-semibold uppercase tracking-[0.14em];
}

.status-pill {
    @apply rounded-full border border-[#DCE5F2] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#4C5E7A] shadow-sm;
}

.button-primary {
    @apply rounded-full px-6 py-3 text-sm font-semibold text-white shadow-md disabled:cursor-not-allowed disabled:bg-gray-500;
    background: var(--main-color);
}

@media (max-width: 1023px) {
    .post-beta-editor-header--with-panel-navigation {
        left: 0;
    }
}
</style>
